import { Modal } from "react-bootstrap";
import { Button } from "./Button";

export type CommitChangesConfirmDialogProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** True when plan is billed by invoice (accounts receivable), false when auto-pay */
  isInvoiced: boolean;
};

export function CommitChangesConfirmDialog({
  show,
  onClose,
  onConfirm,
  isInvoiced,
}: CommitChangesConfirmDialogProps) {
  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm save changes</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isInvoiced ? <InvoicedContent /> : <AutoPayContent />}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" className="btn-main" onClick={onConfirm}>
            Confirm & save
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function InvoicedContent() {
  return (
    <>
      <div className="mb-3">
        <div className="text-muted small">
          The account will be billed for these changes as follows:
        </div>
      </div>

      <div className="fw-semibold mb-2">Billing impact</div>

      <div className="d-flex flex-column gap-2">
        <div className="px-3 py-2 rounded border">
          <div className="fw-medium">Before payment &amp; period start</div>
          <div className="text-muted small">
            If payment has not been made and the billing period has not yet started, the account holder will receive an amended invoice reflecting the updated charges.
          </div>
        </div>

        <div className="px-3 py-2 rounded border">
          <div className="fw-medium">After payment or period started</div>
          <div className="text-muted small">
            If payment has already been made or the period has started, the changes will be reflected on their next invoice.
          </div>
        </div>
      </div>

      <div className="fw-semibold mb-2 mt-3">Summary</div>
      <ul className="mb-0 small text-muted ps-3">
        <li>Not yet paid / period not started - amended invoice sent to account holder</li>
        <li>Already paid or period started - adjustments on their next invoice</li>
      </ul>
    </>
  );
}

function AutoPayContent() {
  return (
    <>
      <div className="mb-3">
        <div className="text-muted small">
          The changes you've made will affect the account's billing as follows:
        </div>
      </div>

      <div className="fw-semibold mb-2">Billing impact</div>

      <div className="d-flex flex-column gap-2">
        <div className="px-3 py-2 rounded border">
          <div className="fw-medium">Next renewal</div>
          <div className="text-muted small">
            The changes will be reflected in the account holder&apos;s next renewal cycle. Their current period charge is already set; their next automatic charge will include these updates.
          </div>
        </div>
      </div>

      <div className="fw-semibold mb-2 mt-3">Summary</div>
      <ul className="mb-0 small text-muted ps-3">
        <li>Changes apply at the account holder&apos;s next renewal</li>
        <li>Current period is unchanged</li>
      </ul>
    </>
  );
}
